'use client';

import clsx from 'clsx';

import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';

import type { StaticImageData } from 'next/image';
import Image from 'next/image';

import { Pause, Play } from '@carbon/icons-react';
import { Column, Grid, IconButton } from '@carbon/react';

import {
  InteractiveButton,
  type Props as InteractiveButtonProps,
} from '@/components/InteractiveButton';
import {
  InteractiveGroup,
  type Props as InteractiveGroupProps,
} from '@/components/InteractiveGroup';
import { Video } from '@/components/Video';
import { useTogglePlayPause } from '@/utils/hooks/useTogglePlayPause';

export type LeadSpaceProps = {
  type?: 'super' | 'medium' | 'short';
  theme?: 'dark' | 'light' | 'white';
  mobileContentPosition?: 'top' | 'bottom';
  children: ReactNode;
  className?: string;
  'data-autotrackregion-namespace'?: string;
};

export function LeadSpace({
  type = 'super',
  theme = 'light',
  mobileContentPosition = 'bottom',
  children,
  className,
  'data-autotrackregion-namespace': dataAutotrackNamespace = 'Leadspace',
}: LeadSpaceProps) {
  const contextValue: LeadSpaceContextValue = { type, theme, mobileContentPosition };

  return (
    <LeadSpaceContext.Provider value={contextValue}>
      <div
        className={clsx(
          'relative overflow-hidden flex flex-col',
          mobileContentPosition === 'top'
            ? 'flex-col-reverse lg:flex-col justify-start'
            : 'justify-end',
          { 'lg:min-h-[var(--leadspace-super-height)]': type === 'super' },
          { 'lg:min-h-[var(--leadspace-medium-height)]': type === 'medium' },
          { 'lg:min-h-[var(--leadspace-short-height)]': type === 'short' },
          className
        )}
        data-autotrackregion-namespace={dataAutotrackNamespace}
      >
        {children}
      </div>
    </LeadSpaceContext.Provider>
  );
}

type LeadSpaceContextValue = {
  type: 'super' | 'medium' | 'short';
  theme: 'dark' | 'light' | 'white';
  mobileContentPosition: 'top' | 'bottom';
};

const LeadSpaceContext = createContext<LeadSpaceContextValue | null>(null);

function useLeadSpace() {
  const context = useContext(LeadSpaceContext);
  if (!context) {
    throw new Error('LeadSpace subcomponents must be used within <LeadSpace>');
  }

  return context;
}

type LeadSpaceImageSources = {
  desktop: StaticImageData;
  md?: StaticImageData;
  mobile?: StaticImageData;
};

type LeadSpaceVideoSource = {
  /**
   * Video URL for large screens (lg breakpoint).
   * Can be shown on all breakpoints if showVideoOnAllBreakpoints is true.
   */
  src: string;
  poster: StaticImageData;
  /**
   * Default: false (video only on lg, images on mobile/md for better performance).
   */
  showOnAllBreakpoints?: boolean;
};

export type LeadSpaceMediaProps = {
  image?: LeadSpaceImageSources;
  video?: LeadSpaceVideoSource;
  className?: string;
};

export function LeadSpaceMedia({ image, video, className }: LeadSpaceMediaProps) {
  const { type, mobileContentPosition } = useLeadSpace();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoError, setVideoError] = useState(false);
  const { togglePlayPause, isPlaying } = useTogglePlayPause(videoRef);

  const showVideo = Boolean(video && !videoError);
  const showVideoOnAll = video?.showOnAllBreakpoints;

  useEffect(() => {
    if (videoRef.current?.error) {
      setVideoError(true);
    }
  }, []);

  return (
    <div className={className}>
      {/* Mobile image */}
      {image?.mobile && !showVideoOnAll && (
        <Image
          className={clsx('w-full block lg:hidden', { 'md:hidden': image.md })}
          src={image.mobile}
          alt=""
          width={typeof image.mobile === 'string' ? 400 : image.mobile.width}
          height={typeof image.mobile === 'string' ? 400 : image.mobile.height}
        />
      )}

      {/* Tablet image */}
      {image?.md && !showVideoOnAll && (
        <div
          className={clsx('hidden md:block lg:hidden relative w-full overflow-hidden z-[1]', {
            'min-h-[var(--leadspace-short-height-tablet)]': type === 'short',
            'min-h-[var(--leadspace-medium-height-tablet)]': type === 'medium',
            'min-h-[var(--leadspace-super-height-tablet)]': type === 'super',
          })}
        >
          <Image
            src={image.md}
            alt=""
            fill
            sizes="100vw"
            //  `unoptimized` is used here to avoid image downscaling on tablet/high-DPR screens,
            //  which caused visible blur compared to the previous CSS background-image approach
            unoptimized
            className="background-img object-cover object-top"
          />
        </div>
      )}

      {/* Desktop image (only when no video) */}
      {image?.desktop && (
        <div
          className={clsx(
            'absolute inset-0 lg:block',
            { hidden: image.mobile },
            { 'md:hidden': image.md }
          )}
        >
          <Image
            src={image.desktop}
            alt=""
            fill
            sizes="100vw"
            //  `unoptimized` is used here to avoid image downscaling on high-DPR screens,
            //  which caused visible blur compared to the previous CSS background-image approach
            unoptimized
            className="background-img object-cover object-center"
          />
        </div>
      )}

      {/* Video */}
      {showVideo && (
        <>
          <Video
            ref={videoRef}
            className={clsx(
              'object-cover object-center w-screen h-full lg:absolute lg:top-0 lg:left-0',
              type === 'short' &&
                'min-h-[var(--leadspace-short-height-mobile)] lg:min-h-[var(--leadspace-short-height)]',
              type === 'medium' &&
                'min-h-[var(--leadspace-medium-height-mobile)] lg:min-h-[var(--leadspace-medium-height)]',
              type === 'super' &&
                'min-h-[var(--leadspace-super-height-mobile)] lg:min-h-[var(--leadspace-super-height)]',
              showVideoOnAll ? 'block' : 'hidden lg:block'
            )}
            src={video!.src}
            poster={video!.poster.src}
            autoPlay
            muted
            loop
          />

          {/* Video controls */}
          <div
            className={clsx(
              'absolute z-[2] bottom-16 right-16 lg:bottom-24 lg:right-24',
              {
                'top-[calc(var(--leadspace-short-height-mobile)-32px)] lg:top-auto':
                  mobileContentPosition === 'bottom' && type === 'short',
              },
              {
                'top-[calc(var(--leadspace-medium-height-mobile)-32px)] lg:top-auto':
                  mobileContentPosition === 'bottom' && type === 'medium',
              },
              showVideoOnAll ? 'block' : 'hidden lg:block'
            )}
          >
            <IconButton
              align="top"
              size="sm"
              kind="secondary"
              label={isPlaying ? 'Pause' : 'Play'}
              onClick={togglePlayPause}
              className="rounded-full"
            >
              {isPlaying ? <Pause /> : <Play />}
            </IconButton>
          </div>
        </>
      )}
    </div>
  );
}

export type LeadSpaceGradientProps = { variant?: 'dark' | 'light' | 'white'; className?: string };

export function LeadSpaceGradient({ variant, className }: LeadSpaceGradientProps) {
  return (
    <div
      className={clsx(
        'hidden lg:block lg:absolute top-0 left-0 w-full h-full z-[1]',
        { 'leadspace-gradient-dark': variant === 'dark' },
        { 'leadspace-gradient-light': variant === 'light' },
        { 'leadspace-gradient-white': variant === 'white' },
        className
      )}
    />
  );
}

export type LeadSpaceContentProps = {
  children: ReactNode;
  contentAlign?: 'center' | 'end';
  className?: string;
};

export function LeadSpaceContent({
  children,
  contentAlign = 'center',
  className,
}: LeadSpaceContentProps) {
  const { type, theme } = useLeadSpace();

  return (
    <div
      className={clsx(
        'z-[1] relative lg:static py-40 md:py-64 lg:p-0 lg:bg-transparent',
        { 'bg-cool-gray-90': theme === 'dark' },
        { 'bg-layer-02': theme === 'white' },
        { 'bg-layer-01': theme === 'light' },
        className
      )}
    >
      <Grid
        className={clsx(
          'lg:relative block lg:grid',
          { 'lg:items-center': contentAlign === 'center' },
          { 'lg:items-end lg:pb-32': contentAlign === 'end' },
          { 'lg:min-h-[var(--leadspace-super-height)]': type === 'super' },
          { 'lg:min-h-[var(--leadspace-medium-height)]': type === 'medium' },
          { 'lg:min-h-[var(--leadspace-short-height)]': type === 'short' }
        )}
      >
        <Column sm={4} md={8} lg={16} className="lg:flex lg:items-center">
          <div
            className={clsx('flex-grow', { 'text-gray-10 [&_h1]:text-gray-10': theme === 'dark' })}
          >
            {children}
          </div>
        </Column>
      </Grid>
    </div>
  );
}

export type LeadSpaceLinksProps = {
  primary?: InteractiveButtonProps;
  secondary?: InteractiveGroupProps['secondary'];
  position?: 'bottom' | 'inline';
  className?: string;
};

export function LeadSpaceLinks({
  primary,
  secondary,
  position = 'bottom',
  className,
}: LeadSpaceLinksProps) {
  const { theme } = useLeadSpace();
  const linksDefaultTheme = theme === 'dark' ? 'dark' : 'light';
  const linksTheme = primary?.theme ?? secondary?.theme ?? linksDefaultTheme;

  if (!primary && !secondary) {
    return null;
  }

  return (
    <div
      className={clsx(
        'mt-40',
        {
          'lg:mt-0 lg:absolute lg:z-[2] lg:bottom-32 lg:left-16 xlg:left-24 lg:pl-16':
            position === 'bottom',
        },
        className
      )}
    >
      {primary && !secondary && <InteractiveButton {...primary} theme={linksTheme} />}

      {primary && secondary && (
        <InteractiveGroup primary={primary} secondary={secondary} theme={linksTheme} />
      )}
    </div>
  );
}
